import { Box, Grid2, MenuItem, Select, TextField } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import {
  checkCanUpdate,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import CustomSwitch from "../../../components/CustomSwitch/CustomSwitch";
import {
  createDrugTypeService,
  fetchDrugTypeByIdService,
  fetchPackageTypeData,
  updateDrugTypeService,
} from "../../../services/drugTypeManagementService";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";
import SearchableSelect from "../../../components/SearchableSelect";

const AddUpdateDrugType = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);
  const [packageTypeList, setPackageTypeList] = useState([]);
  const [canUpdateDrugType, setCanUpdateDrugType] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");
  const role = localStorage.getItem("role");

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  const createDrugTypeSchema = yup.object().shape({
    drugName: yup
      .string()
      .required("Drug Name is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Drug Name must not start with a number or special character"
      )
      .test(
        "has-letter",
        "Drug Name must contain at least one alphabet",
        (value) => /[A-Za-z]/.test(value || "")
      ),
    drugId: yup.string().required("Drug Id is required"),
    packageType: yup.string().required("Package Type is required"),
    fullPillCount: yup
      .string()
      .matches(/^\d+(\.\d+)?$/, "Full Pill Count is not valid.")
      .required("Full Pill Count is required"),
    isBulkDrug: yup.boolean(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createDrugTypeSchema),
    defaultValues: {
      drugName: "",
      drugId: "",
      fullPillCount: "",
      packageType: "",
      isBulkDrug: false,
    },
  });

  useNavigationGuard(isDirty);

  useEffect(() => {
    getPackageTypeListData();
  }, []);

  useEffect(() => {
    if (id && packageTypeList && packageTypeList?.length > 0) {
      getDrugTypeById(id);
    }
  }, [id, packageTypeList]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanEditDrugType"
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateDrugType(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateDrugType(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (canUpdateDrugType) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateDrugType]);

  const getPackageTypeListData = async () => {
    setLoad(true);
    try {
      const res = await fetchPackageTypeData(access_token);
      if (res?.status == 200) {
        setLoad(false);
        setPackageTypeList(res?.data?.result);
      } else {
        setLoad(false);
      }
    } catch (err) {
      console.log(err.message);
      setLoad(false);
    }
  };

  const getDrugTypeById = async (id) => {
    setLoad(true);
    try {
      const res = await fetchDrugTypeByIdService(access_token, id);
      if (res?.status == 200) {
        const resp = res?.data?.result;
        setLoad(false);
        const foundPackageType = packageTypeList?.find(
          (item) => item?.id === resp?.packageId
        );
        reset({
          drugName: resp?.name,
          drugId: resp?.code,
          fullPillCount: resp?.maxReturnablePillCount,
          packageType: foundPackageType?.id,
          isBulkDrug: resp?.isBulk,
        });
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const addUpdateDrugType = async (data) => {
    setLoad(true);
    const newData = {
      name: data.drugName,
      code: data?.drugId,
      isBulk: data?.isBulkDrug,
      packageId: data.packageType,
      maxReturnablePillCount: Number(data?.fullPillCount),
    };

    try {
      if (!id) {
        const res = await createDrugTypeService(access_token, newData);
        if (res?.data?.status == 409) {
          setLoad(false);
          notifyError("Drug Type already exists !");
          return;
        } else if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Drug Type is created successfully !");
          reset();
          navigate("/drug-type-management");
        }
      } else {
        const res = await updateDrugTypeService(access_token, newData, id);
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Drug Type is Updated successfully !");
          reset();
          navigate("/drug-type-management");
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(
                  e,
                  isDirty,
                  "drug-type-management",
                  navigate
                )
              }
            >
              Drug Types
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              to="/addUpdate-drug-type-management"
            >
              {id ? "Edit Drug Type" : "Add Drug Type"}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Drug Type Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addUpdateDrugType)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Drug Name
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    disabled={isReadOnly}
                    fullWidth
                    type={"text"}
                    {...register("drugName")}
                  />
                </div>
                {errors.drugName && (
                  <div className="error-container">
                    {errors.drugName.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Drug Id
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={isReadOnly}
                    type={"text"}
                    {...register("drugId")}
                  />
                </div>
                {errors.drugId && (
                  <div className="error-container">{errors.drugId.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Package Type
                    <span>*</span>
                  </p>
                  {/* <Controller
                    name="packageType"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        id="packageType"
                        fullWidth
                        size="small"
                        disabled={isReadOnly}
                        displayEmpty
                        inputProps={{ "aria-label": "Without label" }}
                        {...field}
                        value={field.value || ""}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {packageTypeList &&
                          packageTypeList?.map((item, index) => (
                            <MenuItem value={item?.id} key={index}>
                              {item?.name}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  /> */}
                  <SearchableSelect
                    control={control}
                    data={packageTypeList}
                    name={"packageType"}
                    optionKey="id"
                    optionLabel="name"
                    isReadOnly={isReadOnly}
                  />
                </div>
                {errors.packageType && (
                  <div className="error-container">
                    {errors.packageType.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Full Pill Count
                    <span>*</span>
                  </p>
                  <Controller
                    name="fullPillCount"
                    control={control}
                    defaultValue="" // or an initial numeric string like "0"
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        size="small"
                        disabled={isReadOnly}
                        type="text"
                        value={field.value}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
                          if (value !== "0") value = value.replace(/^0+/, ""); // Remove leading zeros
                          field.onChange(value); // Update form value
                        }}
                      />
                    )}
                  />
                </div>
                {errors.fullPillCount && (
                  <div className="error-container">
                    {errors.fullPillCount.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Bulk Drug</p>
                  <Controller
                    name="isBulkDrug"
                    control={control}
                    defaultValue={false} // set to true if needed
                    render={({ field }) => (
                      <CustomSwitch
                        isSwitch={field.value}
                        disabled={isReadOnly}
                        setSwitch={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "drug-type-management",
                      navigate
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                {checkCanUpdate(id, isReadOnly, role) && (
                  <button
                    type="submit"
                    disabled={isReadOnly}
                    className={isReadOnly ? "disabled-btn" : "create-btn"}
                  >
                    {id ? "Update" : "Save"}
                  </button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default AddUpdateDrugType;
